/**
 * Datadog RUM Bootstrap Module
 *
 * Initializes Datadog Real User Monitoring with configuration guards.
 * - Skips initialization when config is missing
 * - Warns only in DEV/UAT environments when config is missing
 * - Ensures redaction of sensitive context data
 */

import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import { getDatadogConfig, isDevOrUat } from './datadogConfig';
import { redactSensitiveFields } from './redaction';

const getApiOrigin = (): string | null => {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!apiBaseUrl) return null;
  try {
    return new URL(apiBaseUrl).origin;
  } catch {
    return null;
  }
};

let initialized = false;

/**
 * Initializes Datadog RUM if configuration is available.
 * Safe to call multiple times (idempotent).
 */
export const initRum = (): void => {
  if (initialized) {
    return;
  }

  const config = getDatadogConfig();

  if (!config) {
    if (isDevOrUat()) {
      console.warn(
        '[Observability] Datadog RUM configuration missing. ' +
          'RUM will not be initialized. Set VITE_DATADOG_* environment variables to enable.'
      );
    }
    return;
  }

  try {
    const apiOrigin = getApiOrigin();

    datadogRum.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: config.site,
      service: config.service,
      env: config.env,
      version: import.meta.env.VITE_APP_VERSION || '0.0.0-local',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      ...(apiOrigin && {
        allowedTracingUrls: [
          { match: apiOrigin, propagatorTypes: ['datadog', 'tracecontext'] },
        ],
        traceSampleRate: 100,
      }),
      beforeSend: (event: RumEvent) => {
        if (event.context) {
          event.context = redactSensitiveFields(event.context);
        }
        return true;
      },
    });

    initialized = true;
  } catch (error) {
    // Log error but don't crash the app
    if (isDevOrUat()) {
      console.error('[Observability] Failed to initialize Datadog RUM:', error);
    }
  }
};

/**
 * Returns whether RUM has been successfully initialized.
 */
export const isRumInitialized = (): boolean => {
  return initialized;
};

/**
 * Sets user context for RUM sessions.
 * Standard fields (id, name, email) plus custom attributes (role, company).
 * Context is automatically redacted for sensitive fields.
 */
export const setRumUser = (user: {
  id?: string;
  name?: string;
  email?: string;
  role?: string;
  company?: string;
}): void => {
  if (!initialized) {
    return;
  }

  const safeUser = redactSensitiveFields(user);
  datadogRum.setUser(safeUser);
};

/**
 * Clears user context from RUM sessions.
 * Call this on logout to stop associating RUM data with a user.
 */
export const clearRumUser = (): void => {
  if (!initialized) {
    return;
  }

  datadogRum.clearUser();
};

/**
 * Tracks a custom action in RUM.
 * Context is automatically redacted for sensitive fields.
 */
export const trackRumAction = (
  name: string,
  context?: Record<string, unknown>
): void => {
  if (!initialized) {
    return;
  }

  const safeContext = context ? redactSensitiveFields(context) : undefined;
  datadogRum.addAction(name, safeContext);
};

/**
 * Tracks an error in RUM.
 * Context is automatically redacted for sensitive fields.
 */
export const trackRumError = (
  error: Error,
  context?: Record<string, unknown>
): void => {
  if (!initialized) {
    return;
  }

  const safeContext = context ? redactSensitiveFields(context) : undefined;
  datadogRum.addError(error, safeContext);
};

/**
 * Sets account context for RUM sessions (B2B analytics).
 * Groups users by organization for Product Analytics dashboards.
 * Requires SDK >= 6.4.0.
 */
export const setRumAccount = (account: {
  id: string;
  name?: string;
}): void => {
  if (!initialized) {
    return;
  }

  datadogRum.setAccount(account);
};

/**
 * Clears account context from RUM sessions.
 */
export const clearRumAccount = (): void => {
  if (!initialized) {
    return;
  }

  datadogRum.clearAccount();
};

/**
 * Resets RUM state (for testing purposes only).
 */
export const resetRumState = (): void => {
  initialized = false;
};
